import { GeneralSetupDataType, MonitoringSetupDataType, ScreeningSetupDataType } from './AlertStatusSetupContext';
import {
  InternalApiCustomList,
  InternalApiScreeningAlertStatus,
  InternalApiEntityType,
  InternalApiScenarioType,
} from '../../../api-internal';
import { ApiSanctionType } from '../../../api';

const initialGeneralSetupData: GeneralSetupDataType = {
  name: '',
  code: '',
  isFinal: false,
  isTruePositive: null,
  alertType: {
    isScreeningEnabled: false,
    isMonitoringEnabled: false,
    isBridgeEnabled: false,
    isManualEnabled: false,
  },
  entityType: {
    isPerson: true,
    isTransaction: true,
  },
};

const initialScreeningSetupData: ScreeningSetupDataType = {
  alertSubtype: {
    isSanctions: true,
    isPep: true,
    isAdverseMedia: true,
    isAdverseMediaEntities: true,
    customLists: [],
  },
  isGoodlistable: false,
};

const initialMonitoringSetupData: MonitoringSetupDataType = {
  alertSubtype: {
    isOnline: true,
    isOffline: true,
    isPeriodic: true,
  },
};

export const getInitialSetupData = (
  alertStatus: InternalApiScreeningAlertStatus | undefined,
  customLists: InternalApiCustomList[]
): {
  initialGeneralSetupData: GeneralSetupDataType;
  initialScreeningSetupData: ScreeningSetupDataType;
  initialMonitoringSetupData: MonitoringSetupDataType;
} => {
  if (!alertStatus) {
    return {
      initialGeneralSetupData,
      initialScreeningSetupData: {
        ...initialScreeningSetupData,
        alertSubtype: {
          ...initialScreeningSetupData.alertSubtype,
          customLists: customLists.map((customList) => {
            return {
              id: customList.id!,
              name: customList.name,
              isEnabled: true,
            };
          }),
        },
      },
      initialMonitoringSetupData,
    };
  }

  const generalSetupDataType: GeneralSetupDataType = {
    name: alertStatus.textName,
    code: alertStatus.code,
    isFinal: alertStatus.isFinal,
    isTruePositive: alertStatus.isImportant,
    alertType: {
      isBridgeEnabled: !!alertStatus._configuration?.bridge?.enabled,
      isScreeningEnabled: !!alertStatus._configuration?.screeningLists?.enabled,
      isMonitoringEnabled: !!alertStatus._configuration?.monitoring?.enabled,
      isManualEnabled: !!alertStatus.enabledManual,
    },
    entityType: {
      isTransaction: !!alertStatus._configuration?.entityTypes?.includes(InternalApiEntityType.TRANSACTION),
      isPerson: !!alertStatus._configuration?.entityTypes?.includes(InternalApiEntityType.PERSON),
    },
  };

  const screeningSetupDataType: ScreeningSetupDataType = {
    alertSubtype: {
      isPep: !!alertStatus._configuration?.screeningLists?.restrictedTo?.includes(ApiSanctionType.PEP),
      isSanctions: !!alertStatus._configuration?.screeningLists?.restrictedTo?.includes(ApiSanctionType.SANCTION),
      isAdverseMedia: !!alertStatus._configuration?.screeningLists?.restrictedTo?.includes(
        ApiSanctionType.ADVERSE_MEDIA
      ),
      isAdverseMediaEntities: !!alertStatus._configuration?.screeningLists?.restrictedTo?.includes(
        ApiSanctionType.ADVERSE_MEDIA_ENTITY
      ),
      customLists: customLists.map((customList) => {
        return {
          id: customList.id!,
          name: customList.name,
          isEnabled: !!alertStatus._configuration?.customLists?.restrictedTo?.includes(customList.id!),
        };
      }),
    },
    isGoodlistable: alertStatus.isGoodlistable,
  };

  const monitoringSetupDataType: MonitoringSetupDataType = {
    alertSubtype: {
      isOnline: !!alertStatus._configuration?.monitoring?.restrictedTo?.includes(InternalApiScenarioType.ONLINE),
      isOffline: !!alertStatus._configuration?.monitoring?.restrictedTo?.includes(InternalApiScenarioType.OFFLINE),
      isPeriodic: !!alertStatus._configuration?.monitoring?.restrictedTo?.includes(InternalApiScenarioType.PERIODIC),
    },
  };

  return {
    initialGeneralSetupData: generalSetupDataType,
    initialScreeningSetupData: screeningSetupDataType,
    initialMonitoringSetupData: monitoringSetupDataType,
  };
};

export const setupDataToRequest = ({
  generalSetupData,
  screeningSetupData,
  monitoringSetupData,
}: {
  generalSetupData: GeneralSetupDataType;
  screeningSetupData: ScreeningSetupDataType;
  monitoringSetupData: MonitoringSetupDataType;
}): InternalApiScreeningAlertStatus => {
  const entityTypes: InternalApiEntityType[] = [];
  if (generalSetupData.entityType.isPerson) {
    entityTypes.push(InternalApiEntityType.PERSON);
  }
  if (generalSetupData.entityType.isTransaction) {
    entityTypes.push(InternalApiEntityType.TRANSACTION);
  }

  const screeningAlertSubtypes: ApiSanctionType[] = [];
  if (screeningSetupData.alertSubtype.isSanctions) {
    screeningAlertSubtypes.push(ApiSanctionType.SANCTION);
  }
  if (screeningSetupData.alertSubtype.isPep) {
    screeningAlertSubtypes.push(ApiSanctionType.PEP);
  }
  if (screeningSetupData.alertSubtype.isAdverseMedia) {
    screeningAlertSubtypes.push(ApiSanctionType.ADVERSE_MEDIA);
  }
  if (screeningSetupData.alertSubtype.isAdverseMediaEntities) {
    screeningAlertSubtypes.push(ApiSanctionType.ADVERSE_MEDIA_ENTITY);
  }
  const customLists = screeningSetupData.alertSubtype.customLists
    .filter((customList) => customList.isEnabled)
    .map((customList) => customList.id);
  if (customLists.length) {
    screeningAlertSubtypes.push(ApiSanctionType.CUSTOM_SCREENING_LIST);
  }

  const monitoringAlertSubtypes: InternalApiScenarioType[] = [];
  if (monitoringSetupData.alertSubtype.isOffline) {
    monitoringAlertSubtypes.push(InternalApiScenarioType.OFFLINE);
  }
  if (monitoringSetupData.alertSubtype.isOnline) {
    monitoringAlertSubtypes.push(InternalApiScenarioType.ONLINE);
  }
  if (monitoringSetupData.alertSubtype.isPeriodic) {
    monitoringAlertSubtypes.push(InternalApiScenarioType.PERIODIC);
  }

  return {
    code: generalSetupData.code,
    textName: generalSetupData.name,
    startTime: new Date(),
    enabledScreening: generalSetupData.alertType.isScreeningEnabled,
    enabledMonitoring: generalSetupData.alertType.isMonitoringEnabled,
    enabledBridge: generalSetupData.alertType.isBridgeEnabled,
    enabledManual: generalSetupData.alertType.isManualEnabled,
    isFinal: generalSetupData.isFinal,
    isGoodlistable: screeningSetupData.isGoodlistable,
    isImportant: !!generalSetupData.isTruePositive,
    _configuration: {
      entityTypes,
      screeningLists: {
        enabled: generalSetupData.alertType.isScreeningEnabled,
        restrictedTo: screeningAlertSubtypes,
      },
      customLists: {
        enabled: !!customLists.length,
        restrictedTo: customLists,
      },
      monitoring: {
        enabled: generalSetupData.alertType.isMonitoringEnabled,
        restrictedTo: monitoringAlertSubtypes,
      },
    },
  };
};
